// EventDialog.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { TimeSelect } from '../../ui/TimeSelect';
import { Badge } from '../../ui/badge';
import { toast } from 'sonner';
import { activityAPI, hospitalityAPI, marketingAPI } from '../../../services/api';
import { Pencil, AlertCircle, Calendar, User, Link2, Clock, Sparkles, Trash2, Mail, MessageSquare, CheckCircle2, Upload, Camera, DollarSign, FileText, Gift, Users, Star, Building2, Download, EyeOff } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { associationAPI } from '../../../services/api';
import api from '../../../services/api';

export const EventDialog = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    activity_name: '',
    description: '',
    event_type: 'other',
    company_wide: false,
    all_day: true,
    start_date_time: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_date_time: new Date().toISOString().split('T')[0],
    end_time: '10:00',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.activity_name) {
      toast.error('Event name is required');
      return;
    }

    setLoading(true);
    try {
      let startDateTime, endDateTime;
      
      if (formData.all_day) {
        // For all-day events, use noon to avoid UTC date shifting
        startDateTime = formData.start_date_time + 'T12:00:00';
        endDateTime = (formData.end_date_time || formData.start_date_time) + 'T12:00:00';
      } else {
        // For timed events, combine date and time (store as-is, not UTC)
        startDateTime = formData.start_date_time + 'T' + (formData.start_time || '09:00');
        endDateTime = (formData.end_date_time || formData.start_date_time) + 'T' + (formData.end_time || '10:00');
      }

      await activityAPI.create({
        activity_type: 'event',
        activity_name: formData.activity_name,
        description: formData.description,
        event_type: formData.event_type,
        company_wide: formData.company_wide,
        all_day: formData.all_day,
        start_date_time: startDateTime,
        end_date_time: endDateTime,
      });
      
      toast.success('Event created');
      onSuccess?.();
      onOpenChange(false);
      setFormData({
        activity_name: '',
        description: '',
        event_type: 'other',
        company_wide: false,
        all_day: true,
        start_date_time: new Date().toISOString().split('T')[0],
        start_time: '09:00',
        end_date_time: new Date().toISOString().split('T')[0],
        end_time: '10:00',
      });
    } catch (error) {
      console.error('Failed to create event:', error);
      toast.error('Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New Event</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="event-name">Event Name *</Label>
            <Input
              id="event-name"
              data-testid="event-name-input"
              value={formData.activity_name}
              onChange={(e) => setFormData({ ...formData, activity_name: e.target.value })}
              placeholder="e.g., Team Meeting, Birthday Party"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select 
                value={formData.event_type} 
                onValueChange={(val) => setFormData({ ...formData, event_type: val })}
              >
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="birthday">Birthday</SelectItem>
                  <SelectItem value="race">Race</SelectItem>
                  <SelectItem value="sale">Sale</SelectItem>
                  <SelectItem value="deadline">Deadline</SelectItem>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-4 pt-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="event-company-wide"
                  checked={formData.company_wide}
                  onChange={(e) => setFormData({ ...formData, company_wide: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="event-company-wide" className="text-sm cursor-pointer">
                  Company Wide
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="event-all-day"
                  checked={formData.all_day}
                  onChange={(e) => setFormData({ ...formData, all_day: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="event-all-day" className="text-sm cursor-pointer">
                  All Day
                </Label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-start-date">Start Date</Label>
              <Input
                id="event-start-date"
                data-testid="event-start-date-input"
                type="date"
                value={formData.start_date_time}
                onChange={(e) => setFormData({ ...formData, start_date_time: e.target.value })}
              />
            </div>
            
            {!formData.all_day && (
              <div className="space-y-2">
                <Label htmlFor="event-start-time">Start Time</Label>
                <TimeSelect value={formData.start_time} onChange={v => setFormData({ ...formData, start_time: v })} />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="event-end-date">End Date</Label>
              <Input
                id="event-end-date"
                data-testid="event-end-date-input"
                type="date"
                value={formData.end_date_time}
                onChange={(e) => setFormData({ ...formData, end_date_time: e.target.value })}
              />
            </div>
            
            {!formData.all_day && (
              <div className="space-y-2">
                <Label htmlFor="event-end-time">End Time</Label>
                <TimeSelect value={formData.end_time} onChange={v => setFormData({ ...formData, end_time: v })} />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="event-notes">Notes</Label>
            <Textarea
              id="event-notes"
              data-testid="event-notes-input"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Additional details about the event..."
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="event-submit-btn">
              {loading ? 'Creating...' : 'Create Event'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

